import { Controller } from "@hotwired/stimulus"

// Panneau « Récemment consulté » (cf. RecentVisits::PanelComponent).
//
// Rendu une fois dans le layout : ⌘J / Ctrl+J l'ouvre depuis n'importe quelle page, comme la
// palette ⌘K. Le bouton de l'en-tête ([data-recent-visits-anchor]) vit hors de ce contrôleur ;
// un clic dessus est donc capté au niveau du document. Le panneau s'ancre sous le bouton quand
// il est visible, en haut à droite de l'écran sinon.
//
// L'historique est rechargé à chaque ouverture : la fiche qu'on vient de quitter doit y être.
//
// La consultation d'une fiche est envoyée d'ici, à chaque `turbo:load`, et non enregistrée au
// rendu côté serveur : Turbo précharge les fiches au survol et réutilise ce préchargement au
// clic, sans seconde requête (cf. RecentVisitsHelper#recent_visit_sgid). Le panneau attend cet
// envoi avant de se recharger, et se recharge à sa fin s'il est déjà ouvert.
export default class extends Controller {
  static targets = ["panel", "frame", "shortcut", "error"]
  static values = { url: String, recordUrl: String, open: Boolean }

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    this.boundReposition = this.position.bind(this)
    this.boundBeforeCache = () => this.close()
    this.boundTurboClick = this.handleTurboClick.bind(this)
    this.boundTurboLoad = this.recordVisit.bind(this)
    this.boundFrameLoad = () => this.applyCollapsedGroups({ animate: false })
    this.collapsedGroups = this.readCollapsedGroups()

    document.addEventListener("keydown", this.boundKeydown)
    document.addEventListener("click", this.boundDocumentClick)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    document.addEventListener("turbo:load", this.boundTurboLoad)
    this.element.addEventListener("turbo:click", this.boundTurboClick)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
    this.recordVisit()

    if (this.hasShortcutTarget) this.shortcutTarget.textContent = this.isApplePlatform() ? "⌘J" : "Ctrl+J"
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("click", this.boundDocumentClick)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    document.removeEventListener("turbo:load", this.boundTurboLoad)
    this.element.removeEventListener("turbo:click", this.boundTurboClick)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    this.stopTracking()
  }

  // Le <body> de chaque page porte l'identifiant signé de la fiche affichée (vide ailleurs).
  // `connect` et `turbo:load` peuvent se suivre pour une même page : un seul envoi.
  recordVisit() {
    const sgid = document.body.dataset.recentVisitSgid
    if (!sgid || !this.recordUrlValue || this.lastRecordedBody === document.body) return
    // L'aperçu du cache Turbo n'est pas une consultation : la vraie page suit.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.lastRecordedBody = document.body
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this.pendingRecord = fetch(this.recordUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/x-www-form-urlencoded", "X-CSRF-Token": token || "" },
      body: new URLSearchParams({ sgid })
    })
      .catch(() => {})
      .finally(() => {
        this.pendingRecord = null
        if (this.openValue) this.reload()
      })
  }

  toggle(anchor = null) {
    this.openValue ? this.close() : this.open(anchor)
  }

  open(anchor = null) {
    this.anchor = anchor || this.visibleAnchor()
    this.openValue = true
    this.panelTarget.hidden = false
    this.position()
    this.setAnchorState(true)
    if (!this.pendingRecord) this.reload()
    this.panelTarget.focus({ preventScroll: true })
    window.addEventListener("resize", this.boundReposition)
    window.addEventListener("scroll", this.boundReposition, true)
  }

  close() {
    if (!this.openValue) return

    this.openValue = false
    this.panelTarget.hidden = true
    this.setAnchorState(false)
    this.stopTracking()
  }

  // Toujours sur « Tout », à jour : une frame qui pointe déjà sur l'URL ne se recharge pas
  // d'elle-même.
  reload() {
    if (!this.hasFrameTarget) return

    const url = new URL(this.urlValue, window.location.origin).toString()
    if (this.frameTarget.src === url) {
      this.frameTarget.reload()
    } else {
      this.frameTarget.src = url
    }
  }

  // Périodes repliées (Aujourd'hui, Hier…) : mémorisées pour la session de navigation, et
  // réappliquées à chaque chargement de frame — rechargement du panneau comme page suivante
  // du défilement infini, dont les lignes peuvent prolonger une période déjà repliée.
  toggleGroup({ params: { group } }) {
    const key = String(group)
    this.collapsedGroups.has(key) ? this.collapsedGroups.delete(key) : this.collapsedGroups.add(key)
    this.writeCollapsedGroups()
    this.applyCollapsedGroups()
  }

  // Animé au clic ; sans animation au chargement d'une frame, sinon une page qui arrive dans
  // une période déjà repliée s'ouvrirait puis se refermerait sous les yeux.
  applyCollapsedGroups({ animate = true } = {}) {
    if (!animate) this.element.classList.add("recent-visits--instant")

    this.element.querySelectorAll("[data-recent-visits-group]").forEach((button) => {
      button.setAttribute("aria-expanded", this.collapsedGroups.has(button.dataset.recentVisitsGroup) ? "false" : "true")
    })
    this.element.querySelectorAll("[data-recent-visits-group-items]").forEach((body) => {
      const collapsed = this.collapsedGroups.has(body.dataset.recentVisitsGroupItems)
      body.classList.toggle("is-collapsed", collapsed)
      // Repliées, les lignes restent dans le DOM le temps de l'animation : hors focus et
      // hors lecteur d'écran.
      body.toggleAttribute("inert", collapsed)
    })

    if (!animate) {
      void this.element.offsetHeight
      this.element.classList.remove("recent-visits--instant")
    }
  }

  readCollapsedGroups() {
    try {
      return new Set(JSON.parse(sessionStorage.getItem("depozen.recent-visits.collapsed") || "[]"))
    } catch {
      return new Set()
    }
  }

  writeCollapsedGroups() {
    try {
      sessionStorage.setItem("depozen.recent-visits.collapsed", JSON.stringify([...this.collapsedGroups]))
    } catch {
      // Stockage indisponible (navigation privée) : le repli vaut pour la page en cours.
    }
  }

  // Réponse en erreur (500, réseau) : sans réponse à la frame, Turbo laisserait le squelette
  // indéfiniment. On l'écrit, et une prochaine ouverture retentera.
  showError(event) {
    if (event.target !== this.frameTarget) return

    event.preventDefault()
    if (this.hasErrorTarget) this.frameTarget.replaceChildren(this.errorTarget.content.cloneNode(true))
  }

  position() {
    if (!this.openValue) return

    const panel = this.panelTarget
    const anchor = this.anchor?.isConnected ? this.anchor : null
    const rect = anchor && this.isVisible(anchor) ? anchor.getBoundingClientRect() : null

    panel.classList.toggle("recent-visits__panel--floating", !rect)
    if (rect) {
      // Aligné sur le bord droit du bouton, sans sortir de l'écran à gauche : sur téléphone, le
      // panneau est plus large que la place à gauche du bouton, et son début était rogné.
      const alignedRight = Math.round(window.innerWidth - rect.right)
      const maxRight = window.innerWidth - panel.offsetWidth - 8
      panel.style.top = `${Math.round(rect.bottom + 8)}px`
      panel.style.right = `${Math.max(8, Math.min(alignedRight, maxRight))}px`
    } else {
      panel.style.removeProperty("top")
      panel.style.removeProperty("right")
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.openValue) {
      event.preventDefault()
      this.close()
      this.anchor?.focus?.({ preventScroll: true })
      return
    }

    if (event.key !== "j" && event.key !== "J") return
    if (event.altKey || event.shiftKey) return

    const apple = this.isApplePlatform()
    if (apple ? !event.metaKey || event.ctrlKey : !event.ctrlKey || event.metaKey) return
    // Une modale Bootstrap ou la palette ⌘K ouverte ont priorité.
    if (document.querySelector(".modal.show") || document.body.classList.contains("global-search-palette-open")) return

    event.preventDefault()
    this.toggle()
  }

  handleDocumentClick(event) {
    const anchor = event.target.closest?.("[data-recent-visits-anchor]")
    if (anchor) {
      event.preventDefault()
      this.toggle(anchor)
      return
    }

    if (this.openValue && !this.panelTarget.contains(event.target)) this.close()
  }

  // Une fiche choisie part en visite pleine page : le panneau se referme tout de suite. Pas pour
  // un Cmd+clic (nouvel onglet), que Turbo ne prend pas en charge.
  handleTurboClick(event) {
    if (event.target.closest?.("a")?.dataset.turboFrame === "_top") this.close()
  }

  setAnchorState(open) {
    document.querySelectorAll("[data-recent-visits-anchor]").forEach((button) => {
      button.setAttribute("aria-expanded", open && button === this.anchor ? "true" : "false")
      button.classList.toggle("is-active", open && button === this.anchor)
    })
  }

  visibleAnchor() {
    return Array.from(document.querySelectorAll("[data-recent-visits-anchor]")).find((button) => this.isVisible(button)) || null
  }

  isVisible(element) {
    const rect = element.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight
  }

  stopTracking() {
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
  }

  isApplePlatform() {
    const platform = navigator.userAgentData?.platform || navigator.platform || ""
    return /Mac|iPhone|iPad|iPod/i.test(platform)
  }
}
